import React, { useState } from 'react';
import { Send, Phone, Mail, MessageCircle, Clock, MapPin, CheckCircle } from 'lucide-react';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Textarea } from './ui/textarea';
import { businessInfo, locations } from '../data/mock';
import { trackPixel } from '../lib/pixel';

const ContactSection = () => {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    service: '',
    message: ''
  });
  const [isSubmitted, setIsSubmitted] = useState(false);

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    // Create WhatsApp message
    const message = `Hello! I'm interested in booking a massage.\n\nName: ${formData.name}\nEmail: ${formData.email}\nPhone: ${formData.phone}\nService: ${formData.service}\n\nMessage: ${formData.message}`;
    const whatsappUrl = `https://wa.me/${businessInfo.whatsapp}?text=${encodeURIComponent(message)}`;
    trackPixel('Contact', { content_name: 'Contact Form WhatsApp', method: 'whatsapp' });
    window.open(whatsappUrl, '_blank');
    setIsSubmitted(true);
    setTimeout(() => setIsSubmitted(false), 3000);
  };

  return (
    <section id="contact" className="py-28 bg-luxury-green text-white relative overflow-hidden">
      {/* Decorative Background */}
      <div className="absolute top-0 left-0 w-full h-full opacity-5">
        <div className="absolute top-20 right-20 w-96 h-96 bg-luxury-gold rounded-full blur-3xl" />
        <div className="absolute bottom-20 left-20 w-64 h-64 bg-warm-300 rounded-full blur-3xl" />
      </div>

      <div className="max-w-7xl mx-auto px-6 sm:px-8 lg:px-12 relative">
        {/* Section Header */}
        <div className="text-center max-w-2xl mx-auto mb-16">
          <span className="text-luxury-gold text-xs uppercase tracking-[0.3em] font-medium">
            Get In Touch
          </span>
          <h2 className="font-display text-3xl sm:text-4xl lg:text-5xl mt-4 mb-6">
            Ready to Feel
            <span className="block italic text-luxury-gold/80 mt-1">Your Best?</span>
          </h2>
          <p className="text-white/50 font-light">
            Contact us via WhatsApp for quick responses or fill out the form below.
          </p>
        </div>

        <div className="grid lg:grid-cols-2 gap-12">
          {/* Contact Form */}
          <div className="bg-white/5 backdrop-blur-sm rounded-3xl p-8 border border-white/10">
            <h3 className="text-2xl font-semibold mb-6">Send us a Message</h3>
            
            {isSubmitted ? (
              <div className="flex flex-col items-center justify-center py-12">
                <CheckCircle size={64} className="text-sage-400 mb-4" />
                <p className="text-xl font-medium">Opening WhatsApp...</p>
                <p className="text-stone-400 mt-2">We'll respond as soon as possible!</p>
              </div>
            ) : (
              <form onSubmit={handleSubmit} className="space-y-5">
                <div className="grid sm:grid-cols-2 gap-5">
                  <div>
                    <label className="block text-sm font-medium text-stone-300 mb-2">Name *</label>
                    <Input
                      name="name"
                      value={formData.name}
                      onChange={handleChange}
                      required
                      className="bg-white/10 border-white/20 text-white placeholder:text-stone-500 focus:border-sage-400"
                      placeholder="Your name"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-stone-300 mb-2">Phone *</label>
                    <Input
                      name="phone"
                      type="tel"
                      value={formData.phone}
                      onChange={handleChange}
                      required
                      className="bg-white/10 border-white/20 text-white placeholder:text-stone-500 focus:border-sage-400"
                      placeholder="Your phone number"
                    />
                  </div>
                </div>
                
                <div>
                  <label className="block text-sm font-medium text-stone-300 mb-2">Email</label>
                  <Input
                    name="email"
                    type="email"
                    value={formData.email}
                    onChange={handleChange}
                    className="bg-white/10 border-white/20 text-white placeholder:text-stone-500 focus:border-sage-400"
                    placeholder="your@email.com"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-stone-300 mb-2">Service Interested In</label>
                  <select
                    name="service"
                    value={formData.service}
                    onChange={handleChange}
                    className="w-full bg-white/10 border border-white/20 text-white rounded-md px-4 py-2.5 focus:border-sage-400 focus:outline-none"
                  >
                    <option value="" className="text-stone-800">Select a service...</option>
                    <option value="Deep Tissue Massage" className="text-stone-800">Deep Tissue Massage</option>
                    <option value="Relaxing Massage" className="text-stone-800">Relaxing Massage</option>
                    <option value="Lomi Lomi" className="text-stone-800">Lomi Lomi</option>
                    <option value="Reflexology" className="text-stone-800">Reflexology</option>
                    <option value="Couples Massage" className="text-stone-800">Couples Massage</option>
                    <option value="Other" className="text-stone-800">Other</option>
                  </select>
                </div>

                <div>
                  <label className="block text-sm font-medium text-stone-300 mb-2">Message</label>
                  <Textarea
                    name="message"
                    value={formData.message}
                    onChange={handleChange}
                    rows={4}
                    className="bg-white/10 border-white/20 text-white placeholder:text-stone-500 focus:border-sage-400"
                    placeholder="Tell us about your needs, preferred date/time, or any questions..."
                  />
                </div>

                <Button
                  type="submit"
                  className="w-full bg-sage-600 hover:bg-sage-500 text-white py-6 rounded-full transition-all duration-300 hover:shadow-lg hover:shadow-sage-600/20"
                >
                  <MessageCircle size={20} className="mr-2" />
                  Send via WhatsApp
                </Button>
              </form>
            )}
          </div>

          {/* Contact Info */}
          <div className="space-y-8">
            {/* Quick Contact */}
            <div className="bg-gradient-to-br from-sage-600/20 to-sage-600/5 rounded-3xl p-8 border border-sage-500/20">
              <h3 className="text-xl font-semibold mb-6">Quick Contact</h3>
              
              <div className="space-y-4">
                <a
                  href={`https://wa.me/${businessInfo.whatsapp}`}
                  target="_blank"
                  rel="noopener noreferrer"
                  onClick={() => trackPixel('Contact', { content_name: 'Quick Contact WhatsApp', method: 'whatsapp' })}
                  className="flex items-center gap-4 p-4 bg-green-500/20 hover:bg-green-500/30 rounded-xl transition-colors group"
                >
                  <div className="w-12 h-12 bg-green-500 rounded-full flex items-center justify-center">
                    <MessageCircle size={24} className="text-white" />
                  </div>
                  <div>
                    <p className="font-medium">WhatsApp</p>
                    <p className="text-stone-400 text-sm group-hover:text-green-400 transition-colors">{businessInfo.phone}</p>
                  </div>
                </a>

                <a
                  href={`tel:${businessInfo.phone}`}
                  onClick={() => trackPixel('Contact', { content_name: 'Quick Contact Phone', method: 'phone' })}
                  className="flex items-center gap-4 p-4 bg-white/5 hover:bg-white/10 rounded-xl transition-colors"
                >
                  <div className="w-12 h-12 bg-sage-600 rounded-full flex items-center justify-center">
                    <Phone size={24} className="text-white" />
                  </div>
                  <div>
                    <p className="font-medium">Phone</p>
                    <p className="text-stone-400 text-sm">{businessInfo.phone}</p>
                  </div>
                </a>

                <a
                  href={`mailto:${businessInfo.email}`}
                  className="flex items-center gap-4 p-4 bg-white/5 hover:bg-white/10 rounded-xl transition-colors"
                >
                  <div className="w-12 h-12 bg-warm-600 rounded-full flex items-center justify-center">
                    <Mail size={24} className="text-white" />
                  </div>
                  <div>
                    <p className="font-medium">Email</p>
                    <p className="text-stone-400 text-sm">{businessInfo.email}</p>
                  </div>
                </a>
              </div>
            </div>

            {/* Hours */}
            <div className="bg-white/5 rounded-2xl p-6 border border-white/10">
              <div className="flex items-center gap-3 mb-4">
                <Clock size={24} className="text-sage-400" />
                <h4 className="font-semibold">Opening Hours</h4>
              </div>
              <p className="text-stone-300">{businessInfo.hours}</p>
              <p className="text-stone-500 text-sm mt-2">Open every day of the week</p>
            </div>

            {/* Locations Quick Links */}
            <div className="bg-white/5 rounded-2xl p-6 border border-white/10">
              <div className="flex items-center gap-3 mb-4">
                <MapPin size={24} className="text-sage-400" />
                <h4 className="font-semibold">Our Locations</h4>
              </div>
              <div className="space-y-3">
                {locations.map((loc) => (
                  <a
                    key={loc.id}
                    href={loc.mapUrl}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="block text-stone-300 hover:text-sage-400 transition-colors"
                  >
                    <span className="font-medium">{loc.name}</span> - {loc.area}
                  </a>
                ))}
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default ContactSection;
